import {
  getKidTemplateTypeRank,
  KidTemplateSetupStatus,
  KidTemplateType,
} from '../../../../api/kids/types';

export const TEMPLATE_TYPES: KidTemplateType[] = [
  KidTemplateType.MANCO,
  KidTemplateType.SUB_FUND,
  KidTemplateType.FUND_MANAGER,
  KidTemplateType.CIU_FUND,
  KidTemplateType.ADMINISTRATOR,
];

/**
 * Edit-details type downgrade. Off until the backend contract and
 * mechanics are stable; flip to true to restore the type editor.
 */
export const ENABLE_TEMPLATE_TYPE_CHANGE = false;

/** Broadest → most specific (matches template type rank / info tooltip). */
export const sortTemplateTypesByRank = (
  types: readonly KidTemplateType[] = TEMPLATE_TYPES,
): KidTemplateType[] =>
  [...types].sort(
    (a, b) => getKidTemplateTypeRank(b) - getKidTemplateTypeRank(a),
  );

export const KID_TEMPLATE_TYPE_VALUE_SET = new Set<string>(
  Object.values(KidTemplateType) as string[],
);

export const UNKNOWN_TEMPLATE_GROUP_KEY = 'unknown';

export const formatKidTemplateTypeLabel = (typeKey: string): string => {
  if (typeKey === UNKNOWN_TEMPLATE_GROUP_KEY) return 'Type not set';
  if (typeKey === KidTemplateType.CIU_FUND) return 'CIU Fund';
  return typeKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Plain-language label for the entity a scoped template applies to. */
export const getTemplateReferenceFieldLabel = (
  templateType: KidTemplateType,
): string => {
  switch (templateType) {
    case KidTemplateType.SUB_FUND:
      return 'Sub-fund';
    case KidTemplateType.FUND_MANAGER:
      return 'Fund manager';
    case KidTemplateType.CIU_FUND:
      return 'CIU';
    case KidTemplateType.ADMINISTRATOR:
      return 'Administrator';
    default:
      return 'Applies to';
  }
};

/** Subtitle shown when creating a template of the given type. */
export const formatCreateTemplateSubtitle = (
  templateType: KidTemplateType,
): string => {
  switch (templateType) {
    case KidTemplateType.MANCO:
      return 'Applies to all documents.';
    case KidTemplateType.SUB_FUND:
      return 'Choose the sub-fund this template should apply to.';
    case KidTemplateType.FUND_MANAGER:
      return 'Choose the fund manager this template should apply to.';
    case KidTemplateType.CIU_FUND:
      return 'Choose the CIU this template should apply to.';
    case KidTemplateType.ADMINISTRATOR:
      return 'Choose the administrator this template should apply to.';
    default:
      return 'Choose what this template should apply to.';
  }
};

/** Scope description shown when editing an existing template. */
export const formatEditTemplateScopeSubtitle = (
  templateType: KidTemplateType,
  referenceName?: string | null,
): string => {
  switch (templateType) {
    case KidTemplateType.MANCO:
      return 'Applies to all documents.';
    case KidTemplateType.SUB_FUND:
      return referenceName
        ? `Scoped to sub-fund ${referenceName}.`
        : 'Scoped to a specific sub-fund.';
    case KidTemplateType.FUND_MANAGER:
      return referenceName
        ? `Scoped to fund manager ${referenceName}.`
        : 'Scoped to a specific fund manager.';
    case KidTemplateType.CIU_FUND:
      return referenceName
        ? `Scoped to CIU ${referenceName}.`
        : 'Scoped to a specific CIU.';
    case KidTemplateType.ADMINISTRATOR:
      return referenceName
        ? `Scoped to administrator ${referenceName}.`
        : 'Scoped to a specific administrator.';
    default:
      return 'Template scope could not be determined.';
  }
};

const LEGACY_TEMPLATE_SETUP_STATUS: Record<string, KidTemplateSetupStatus> = {
  editing: KidTemplateSetupStatus.INCOMPLETE,
  edited: KidTemplateSetupStatus.COMPLETE,
};

/** Map API/legacy setup status strings onto {@link KidTemplateSetupStatus}. */
export function normalizeTemplateSetupStatus(
  raw: string | null | undefined,
): string | null {
  if (typeof raw !== 'string') return null;
  const status = raw.trim().toLowerCase();
  if (!status) return null;
  return LEGACY_TEMPLATE_SETUP_STATUS[status] ?? status;
}

export const formatTemplateSetupStatusLabel = (
  status: string | null | undefined,
): string => {
  const normalized = normalizeTemplateSetupStatus(status);
  if (!normalized) return 'unknown';
  return normalized;
};

export const templateSetupStatusBadgeClass = (
  status: string | null | undefined,
): string => {
  const normalized = normalizeTemplateSetupStatus(status);
  if (normalized === KidTemplateSetupStatus.INCOMPLETE) {
    return 'bg-amber-100 text-amber-950 ring-amber-200';
  }
  if (normalized === KidTemplateSetupStatus.COMPLETE) {
    return 'bg-emerald-100 text-emerald-950 ring-emerald-200';
  }
  if (normalized === KidTemplateSetupStatus.INVALID) {
    return 'bg-rose-100 text-rose-950 ring-rose-200';
  }
  return 'bg-gray-100 text-gray-700 ring-gray-200';
};

export const inputClassName =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue';

export const inputErrorClassName =
  'border-red-500 focus:border-red-500 focus:ring-red-500';

/** Responsive width: up to 36rem on wide screens, full available width when narrower. */
export const formDropdownWidthClassName = 'w-full max-w-[36rem] min-w-0';

/** Dropdown fills its grid/flex cell (no 36rem cap). */
export const formDropdownFullBleedClassName = 'w-full min-w-0';

export const formDropdownClassName = `${formDropdownWidthClassName} inline-flex h-9 items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-0 text-sm font-medium text-gray-800 shadow-sm data-[hover]:bg-gray-50 data-[open]:bg-gray-50`;

export const formDropdownFullBleedButtonClassName =
  `${formDropdownFullBleedClassName} inline-flex h-9 items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-0 text-sm font-medium text-gray-800 shadow-sm data-[hover]:bg-gray-50 data-[open]:bg-gray-50`;

export const formDropdownMenuClassName =
  '[--anchor-max-height:16rem] w-[var(--button-width)] max-w-[calc(100vw-2rem)]';

/** Compact dropdown styling for the all-templates list toolbar (not full-width form fields). */
export const listToolbarDropdownClassName =
  'inline-flex h-9 w-auto min-w-[11rem] max-w-none items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-0 text-sm font-medium text-gray-800 shadow-sm data-[hover]:bg-gray-50 data-[open]:bg-gray-50';

export const listToolbarDropdownMenuClassName =
  '[--anchor-max-height:16rem] w-[var(--button-width)] min-w-[11rem] max-w-[calc(100vw-2rem)]';

export const listToolbarCreateDropdownClassName =
  'inline-flex w-auto shrink-0 justify-between rounded-md bg-mainblue px-3 py-2 text-sm font-semibold text-white shadow-sm data-[hover]:bg-gray-700 data-[open]:bg-gray-700';

export const formDropdownCompactClassName =
  'min-w-0 flex-1 justify-between rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-sm data-[hover]:bg-gray-50 data-[open]:bg-gray-50';

const btnBase =
  'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-45';

export const btnPrimary = `${btnBase} bg-mainblue text-white hover:bg-mainblue_hover focus:ring-mainblue`;
export const btnSecondary = `${btnBase} border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:ring-gray-400`;
export const btnDanger = `${btnBase} border border-red-200 bg-red-50 text-red-900 hover:bg-red-100 focus:ring-red-400`;
